/**
 * accountLinking — make email the deciding factor across ALL sign-in methods.
 *
 * Firebase's "Link accounts that use the same email" (one account per email)
 * setting does NOT auto-link two different federated providers: signing in
 * with provider B (e.g. Apple) using an email already tied to provider A
 * (e.g. Google) throws `auth/account-exists-with-different-credential` instead
 * of merging. This is symmetric — it happens whichever provider is tried
 * second (Apple→existing-Google AND Google→existing-Apple, etc.).
 *
 * To honour "same email → same user" we finish the link ourselves:
 *
 *   1. On the conflict, stash the pending (just-attempted) OAuth credential
 *      (`stashOAuthConflict`) and ask the user to sign in with the method they
 *      used before (`login.accountLinkPrompt`).
 *   2. When they re-authenticate, the auth-state listener calls
 *      `linkPendingCredential(user)`, which attaches the stashed provider to
 *      that existing account — so both methods work from then on.
 *
 * The credential lives in sessionStorage (serialized) so it survives the
 * redirect round-trip used by standalone PWAs. OAuth ID tokens expire within
 * minutes; a stale link attempt fails harmlessly and the user can retry.
 *
 * Scope note: this auto-links OAuth↔OAuth (Google ↔ Apple), both directions.
 * The email-link method's one-time code is consumed on the failed attempt, so
 * an email-link *attempt* against an existing OAuth account only shows the
 * prompt (the user then signs in with their OAuth method and lands on the same
 * account) — it is not auto-linked.
 */
import { OAuthProvider, GoogleAuthProvider, linkWithCredential } from "firebase/auth";
import { sessionStore } from "./safeStorage";

const KEY = "sendly-pending-cred";

/**
 * Extract and stash the OAuth credential carried by an account-exists conflict
 * error, regardless of which provider was attempted (google.com / apple.com).
 */
export function stashOAuthConflict(err) {
  let cred = null;
  try { cred = OAuthProvider.credentialFromError(err); } catch (_) { /* not OAuth */ }
  if (!cred) { try { cred = GoogleAuthProvider.credentialFromError(err); } catch (_) { /* ignore */ } }
  if (!cred) return;
  let payload;
  try { payload = { json: cred.toJSON(), providerId: cred.providerId }; } catch (_) { return; }
  sessionStore.setJSON(KEY, payload);
}

/**
 * Link a stashed credential to the just-signed-in user. Call from the
 * auth-state listener after any successful sign-in. No-op (and clears the
 * stash) when there is nothing pending or that provider is already linked.
 */
export async function linkPendingCredential(user) {
  if (!user) return false;
  const raw = sessionStore.get(KEY);
  if (!raw) return false;
  sessionStore.remove(KEY);
  let obj;
  try { obj = JSON.parse(raw); } catch { return false; }
  // Already linked — nothing to do.
  if (obj.providerId && user.providerData?.some(p => p.providerId === obj.providerId)) return false;
  try {
    const cred = OAuthProvider.credentialFromJSON(obj.json);
    if (!cred) return false;
    await linkWithCredential(user, cred);
    return true;
  } catch (e) {
    // Expired token / credential-already-in-use: harmless — the user can retry
    // the second provider, which will now resolve to the linked account.
    console.warn("[account-link] could not link pending credential:", e?.code || e?.message);
    return false;
  }
}
